import React from 'react';
import { Controller } from 'react-hook-form';
import { TextField } from '@mui/material';
import { FormInputProps } from './form-input-props';
import { numericToStringFormatter } from '../../../common-sdk/utils/numeric-to-string.formatter';

export interface FormInputNumericProps extends FormInputProps {
  precision?: number;
  min?: number;
  max?: number;
}

function valueAsNumber(value: string): number {
  if (!value || value.trim() === '') return 0;
  const normalized = value.replace(/,/g, '.');
  return Number(normalized);
}

function formatForSubmission(value: string, precision = 0): string {
  if (!value || value.trim() === '') return '';
  const numValue = valueAsNumber(value);
  return numValue.toFixed(precision);
}

export const FormInputNumeric = (props: FormInputNumericProps) => {
  const precision = props.precision != null ? props.precision : 0;

  function formatValue(value: string, onChange: (...event: any[]) => void) {
    if (value !== null && value !== undefined && `${value}`.trim() !== '') {
      const val = value.replace(/[^\d,.-]/g, '');

      let cleanedVal = val;
      if (val.startsWith('00')) {
        cleanedVal = val.replace(/^0+/, '0');
      }

      let transformedValue = numericToStringFormatter(cleanedVal, precision);

      if (props.max != null || props.min != null) {
        const valAsNumber = valueAsNumber(transformedValue);

        if (props.max != null && valAsNumber > props.max) {
          transformedValue = numericToStringFormatter(props.max.toString(), precision);
        }

        if (props.min != null && valAsNumber < props.min) {
          transformedValue = numericToStringFormatter(props.min.toString(), precision);
        }
      }

      onChange(transformedValue);
    } else {
      onChange('');
    }
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>, onChange: (...event: any[]) => void) {
    onChange(event.target.value);
  }

  function handleBlur(onChange: (...event: any[]) => void, value: any): void {
    formatValue(value?.toString(), onChange);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>, onChange: (...event: any[]) => void, value: any) {
    if (event.key === 'Enter') {
      formatValue(value?.toString(), onChange);
    }
  }

  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field: { onChange, value, onBlur }, fieldState: { error }, formState }) => {
        const displayValue = value !== undefined && value !== null ? value.toString().replace(/\./g, ',') : '';
        return (
          <div data-test-id={`input-${props.name}-wrapper`}>
            <TextField
              inputProps={{
                'data-test-id': `input-${props.name}`,
                // Add formatted value to the form data in period format for API submission
                'data-api-value': formatForSubmission(value?.toString() || '', precision),
              }}
              helperText={error ? error.message : props.helperText ? props.helperText : ''}
              size='small'
              error={!!error}
              value={displayValue}
              fullWidth
              label={props.label}
              variant={props.variant != null ? props.variant : 'outlined'}
              margin='normal'
              onBlur={() => handleBlur(onChange, value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, onChange)}
              onKeyPress={(event) => handleKeyDown(event, onChange, value)}
              disabled={props.isDeactivated}
            />
          </div>
        );
      }}
    />
  );
};
